import { postData } from '../utils/ajax-utils';
import initBtnLoading, { BtnLoadingResetEvent } from './btn-loading';
import { ModalHTMLElement } from './modal';

export function initConvertToSSLAcmeToken(): void {
    // Récupération des éléments composant la modal de conversion
    const $modal = document.querySelector<ModalHTMLElement>('.js-convert-to-ssl-acme-token-modal');
    const $modalContent = $modal?.querySelector<HTMLElement>('.modal-content');
    const $modalTemplate = $modal?.querySelector<HTMLTemplateElement>('#modal-content-template');

    if (!$modal || !$modalContent || !$modalTemplate) {
        return;
    }

    const modal: Modal = {
        $el: $modal,
        $content: $modalContent,
        $template: $modalTemplate,
    };

    // Récupération des boutons permettant d'ouvrir la modal de conversion
    const $buttons = document.querySelectorAll<HTMLButtonElement>('.js-convert-to-ssl-acme-token-btn');
    const buttons: Button[] = [];

    $buttons.forEach($button => {
        const thirdpartyRefExt = $button.dataset.thirdpartyRefExt;
        const contactRefExt = $button.dataset.contactRefExt;
        const serviceRef = $button.dataset.serviceRef;
        const serviceLabel = $button.dataset.serviceLabel;
        const nbInStock = parseInt($button.dataset.nbInStock ?? '0');
        const changeRate = parseInt($button.dataset.changeRate ?? '0');
        const changeRateMax = changeRate * nbInStock;

        if (!thirdpartyRefExt || !contactRefExt || !serviceRef || !serviceLabel || !nbInStock || !changeRate) {
            return;
        }

        buttons.push({
            $el: $button,
            thirdpartyRefExt,
            contactRefExt,
            serviceRef,
            serviceLabel,
            nbInStock,
            changeRate,
            changeRateMax,
        });
    });

    new ConvertToSSLAcmeToken(modal, buttons);
}

type Modal = {
    $el: ModalHTMLElement;
    $content: HTMLElement;
    $template: HTMLTemplateElement;
    step?: ModalStep;
}

type ModalStep = {
    begin: {
        $el: HTMLElement;
        $form: HTMLFormElement;
        $cancelBtn: HTMLButtonElement;
        $confirmBtn: HTMLButtonElement;
    };
    confirmation: {
        $el: HTMLElement;
        $errorContainer: HTMLUListElement;
        defaultError: string,
        $cancelBtn: HTMLButtonElement;
        $confirmBtn: HTMLButtonElement;
    };
    loading: {
        $el: HTMLElement;
    };
    success: {
        $el: HTMLElement;
    }
};

type Button = {
    $el: HTMLButtonElement;
    thirdpartyRefExt: string; // RefExt de la Thirdparty de l'utilisateur effectuant l'action
    contactRefExt: string; // RefExt de l'utilisateur effectuant l'action
    serviceRef: string; // Référence du produit
    serviceLabel: string; // Nom du produit
    nbInStock: number; // Nombre de certificats en stock
    changeRate: number; // Nombre de jetons ACME obtenus pour la conversion d'1 certificat en stock
    changeRateMax: number; // Nombre max de jetons ACME qu'il est possible d'obtenir pour la conversion de tous les certificats en stock
}

type FormResponse = {
    from: number,
    to: number,
}

class ConvertToSSLAcmeToken {
    modal: Modal;
    buttons: Button[];

    constructor(modal: Modal, buttons: Button[]) {
        this.modal = modal;
        this.buttons = buttons;

        this.buttons.forEach(button => {
            button.$el.addEventListener('click', () => {
                this.openModal(button);
            });
        });
    }

    openModal(button: Button): void {
        this.showModalCloseButton();
        this.mount(button);
        this.modal.$el.open();
    }

    hideModalCloseButton(): void {
        this.modal.$el.classList.add('locked-force');
        this.modal.$el.querySelector('.modal-container > .modal-close')?.classList.add('hidden');
    }

    showModalCloseButton(): void {
        this.modal.$el.classList.remove('locked-force');
        this.modal.$el.querySelector('.modal-container > .modal-close')?.classList.remove('hidden');
    }

    mount(button: Button): void {
        this.modal.$content.innerHTML = this.modal.$template.innerHTML
            .replace(/%nb_in_stock%/gi, '' + button.nbInStock)
            .replace(/%change_rate%/gi, '' + button.changeRate)
            .replace(/%change_rate_max%/gi, '' + button.changeRateMax)
            .replace(/%service_label%/gi, button.serviceLabel);

        this.modal.step = {
            begin: {
                $el: this.modal.$content.querySelector('[data-step-begin]') as HTMLElement,
                $form: this.modal.$content.querySelector('[data-step-begin] [data-form]') as HTMLFormElement,
                $cancelBtn: this.modal.$content.querySelector('[data-step-begin] [data-cancel]') as HTMLButtonElement,
                $confirmBtn: this.modal.$content.querySelector('[data-step-begin] [data-confirm]') as HTMLButtonElement,
            },
            confirmation: {
                $el: this.modal.$content.querySelector('[data-step-confirmation]') as HTMLElement,
                $errorContainer: this.modal.$content.querySelector('[data-step-confirmation] [data-form-error]') as HTMLUListElement,
                defaultError: (this.modal.$content.querySelector('[data-step-confirmation] [data-form-error]') as HTMLUListElement).dataset.default as string,
                $cancelBtn: this.modal.$content.querySelector('[data-step-confirmation] [data-cancel]') as HTMLButtonElement,
                $confirmBtn: this.modal.$content.querySelector('[data-step-confirmation] [data-confirm]') as HTMLButtonElement,
            },
            loading: {
                $el: this.modal.$content.querySelector('[data-step-loading]') as HTMLElement,
            },
            success: {
                $el: this.modal.$content.querySelector('[data-step-success]') as HTMLElement,
            },
        };

        this.bindEventsOfStepBegin(button);
    }

    bindEventsOfStepBegin(button: Button): void {
        if (!this.modal.step) {
            return;
        }

        this.modal.step.begin.$cancelBtn.addEventListener('click', () => {
            this.modal.$el.close();
        });

        const $fromInput = this.modal.step.begin.$form.querySelector('input[name="from"]') as HTMLInputElement;
        const $toInput = this.modal.step.begin.$form.querySelector('input[name="to"]') as HTMLInputElement;

        $fromInput.addEventListener('input', () => {
            $toInput.value = '' + Math.round((parseInt($fromInput.value || '0') * button.changeRate));
        });

        $toInput.addEventListener('input', () => {
            $fromInput.value = '' + Math.round(parseInt($toInput.value || '0') / button.changeRate);
        });

        this.modal.step.begin.$confirmBtn.addEventListener('click', () => {
            this.validateForm(button).then(response => {
                this.openConfirmationStep(button, response as FormResponse);
            }).catch(() => null);
        });
    }

    validateForm(button: Button): Promise<void|FormResponse> {
        const formData = new FormData((this.modal.step as ModalStep).begin.$form);
        const from = parseInt(formData.get('from') as string || '0');
        const to = parseInt(formData.get('to') as string || '0');

        if ((from <= 0 || from > button.nbInStock) || // Si la valeur de `from` est erronée
            (to <= 0 || to > button.changeRateMax) || // Si la valeur de `to` est erronée
            (from * button.changeRate !== to) // Si le calcul est erroné
        ) {
            return Promise.reject();
        }

        return Promise.resolve({ from, to });
    }

    openConfirmationStep(button: Button, formResponse: FormResponse, withEvents: boolean = true): void {
        if (withEvents) {
            this.hideModalCloseButton();

            this.bindEventsOfStepConfirmation(button, formResponse);
        }

        this.modal.step?.begin.$el.classList.add('hidden');
        this.modal.step?.confirmation.$el.classList.remove('hidden');
    }

    bindEventsOfStepConfirmation(button: Button, formResponse: FormResponse): void {
        if (!this.modal.step) {
            return;
        }

        initBtnLoading(this.modal.step.confirmation.$el);

        this.modal.step.confirmation.$cancelBtn.addEventListener('click', () => {
            this.modal.$el.close(true);
        });

        this.modal.step.confirmation.$confirmBtn.addEventListener('click', () => {
            this.openLoadingStep();
            (this.modal.step as ModalStep).confirmation.$errorContainer.innerHTML = '';

            this.submit(button, formResponse).then(response => {
                if (response.success) {
                    this.openSuccessStep();
                } else {
                    this.closeLoadingStep();
                    this.openConfirmationStep(button, formResponse, false);
                    (this.modal.step as ModalStep).confirmation.$errorContainer.innerHTML = `<li>${response.data}</li>`;
                }
            }).catch(() => {
                this.closeLoadingStep();
                this.openConfirmationStep(button, formResponse, false);
                const defaultError = (this.modal.step as ModalStep).confirmation.defaultError;
                (this.modal.step as ModalStep).confirmation.$errorContainer.innerHTML = `<li>${defaultError}</li>`;
            }).finally(() => {
                (this.modal.step as ModalStep).confirmation.$confirmBtn.dispatchEvent(BtnLoadingResetEvent);
                (this.modal.step as ModalStep).confirmation.$cancelBtn.dispatchEvent(BtnLoadingResetEvent);
            });
        });
    }

    async submit(button: Button, formResponse: FormResponse): Promise<{ success: boolean, data: string }> {
        return postData(scriptVar.ajaxUrl, {
            action: 'convert_to_ssl_acme_token',
            thirdparty_ref_ext: button.thirdpartyRefExt,
            contact_ref_ext: button.contactRefExt,
            service_ref: button.serviceRef,
            nb_to_convert: '' + formResponse.from,
        });
    }

    openLoadingStep(): void {
        this.modal.step?.loading.$el.classList.remove('hidden');
        this.modal.step?.confirmation.$el.classList.add('hidden');
    }

    closeLoadingStep(): void {
        this.modal.step?.loading.$el.classList.add('hidden');
        this.modal.step?.confirmation.$el.classList.remove('hidden');
    }

    openSuccessStep(): void {
        this.modal.step?.loading.$el.classList.add('hidden');
        this.modal.step?.success.$el.classList.remove('hidden');
    }
}
